import { useEffect, useState } from 'react'

import { Box, Skeleton } from '@factorialco/deprecated-design-system'
import { TimeoffRootPageEmployeeDataFragment } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useFetchData } from 'modules/timeoff/components/Card/useFetchData'
import PolicyAssignments from 'modules/timeoff/components/Leaves/PolicyAssignments'
import { hasPolicyTemporality } from 'modules/timeoff/lib/features/TimeoffFeatures'
import policyTimelines from 'modules/timeoff/store/collections/timeoffPolicies/policyTimelines'
import type { PolicyTimelineItem } from 'modules/timeoff/store/models/TimeoffPolicy/PolicyTimeline'

import AllowanceIncidences from './AllowanceIncidences'
import Calendar from './Calendar'
import CalendarSkeletonLoader from './Calendar/SkeletonLoader'
import LeavesList from './LeavesList'
import LeavesListSkeletonLoader from './LeavesList/Section/SkeletonLoader'
import Overview from './Overview'
import OverviewSkeletonLoader from './Overview/LegacyStatsCounter/SkeletonLoader'
import './index.styles.css'

interface Props {
  employeeData: TimeoffRootPageEmployeeDataFragment
  hasManualAdjustmentOptions: boolean
}

const Leaves = ({ employeeData, hasManualAdjustmentOptions }: Props) => {
  const year = moment().year()
  const [selectedTimelineItem, setSelectedTimelineItem] = useState<
    PolicyTimelineItem | undefined | null
  >(null)
  const employeeId = employeeData.id
  const employeeCanEditPolicyAssignments =
    employeeData._policies?.editPolicyAssignments.can ?? false
  const employeeIsTerminated =
    employeeData.terminatedOn !== null && employeeData.terminatedOn !== undefined
  // NOTE: review this. The attendable attribute seems deprecated
  const employeeIsIncludedInTimeTracking = employeeData.attendable
  const employeePreferredName = employeeData.preferredName || employeeData.fullName
  const employeeCanApproveLeaves = employeeData._policies?.approveLeaves.can ?? false
  const employeeCanUpdateLeaves = employeeData._policies?.updateLeaves.can ?? false
  const employeeCanManageLeaveDocuments = employeeData._policies?.manageLeavesDocuments.can ?? false
  const employeeCanCreateLeaves = employeeData._policies?.createLeaves.can ?? false
  const employeeCanManageIncidences = employeeData._policies?.manageIncidences.can ?? false
  const employeeTimeoffPolicyId = employeeData.timeoffPolicy?.id

  const { loading } = useFetchData(
    employeeData.id,
    employeeData.timeoffPolicy?.id,
    employeeData.locationId,
    year
  )

  useEffect(() => {
    if (policyTimelines.length > 0) {
      setSelectedTimelineItem(policyTimelines?.timeline?.current)
    }
  }, [loading, policyTimelines.length])

  if (loading) {
    return (
      <Box flexDirection="row" flexGrow>
        <Box className={'Leaves_column'} gap="s32" paddingRight="s32">
          <OverviewSkeletonLoader />
          <LeavesListSkeletonLoader />
        </Box>
        <Box className={'Leaves_calendar'}>
          <CalendarSkeletonLoader />
          <Box paddingX="s32" paddingY="s32" flexDirection="row">
            <Skeleton height="s200" />
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box flexDirection="row" flexGrow>
      <Box className={'Leaves_column'} gap="s32" paddingRight="s32">
        {hasPolicyTemporality() && (
          <PolicyAssignments
            employeeId={employeeId}
            employeeCanEditPolicyAssignments={employeeCanEditPolicyAssignments}
            selectedTimelineItem={selectedTimelineItem}
            setSelectedTimelineItem={setSelectedTimelineItem}
          />
        )}
        <Overview
          employeeId={employeeId}
          employeeIsTerminated={employeeIsTerminated}
          employeeIsIncludedInTimeTracking={employeeIsIncludedInTimeTracking}
          policyTimelineItem={selectedTimelineItem}
        />
        <LeavesList
          employeeId={employeeId}
          employeePreferredName={employeePreferredName}
          employeeCanApproveLeaves={employeeCanApproveLeaves}
          employeeCanUpdateLeaves={employeeCanUpdateLeaves}
          employeeCanManageLeaveDocuments={employeeCanManageLeaveDocuments}
          employeeTimeoffPolicyId={employeeTimeoffPolicyId}
          year={year}
        />
      </Box>
      <Box className={'Leaves_calendar'}>
        <Calendar
          employeeId={employeeId}
          employeeCanCreateLeaves={employeeCanCreateLeaves}
          employeeCanApproveLeaves={employeeCanApproveLeaves}
          year={year}
        />
        <AllowanceIncidences
          employeeId={employeeId}
          employeeTimeoffPolicyId={employeeTimeoffPolicyId}
          employeeCanManageIncidences={employeeCanManageIncidences}
          timelineItem={selectedTimelineItem}
          hasManualAdjustmentOptions={hasManualAdjustmentOptions}
        />
      </Box>
    </Box>
  )
}

// Set displayName for better debugging in React DevTools and error messages
Leaves.displayName = 'Leaves'

export default observer(Leaves)
